import React, { useContext, useRef, useState } from 'react';
import Keycloak from 'keycloak-js';
import jwt_decode from 'jwt-decode';
import {
    TOKEN_MIN_VALIDITY_BEFORE_EXPIRATION,
    TOKEN_MIN_VALIDITY_TO_FORCE_UPDATE,
} from '../../constants';

interface TokenData {
    exp: number;
    iat: number;
    auth_time: number;
    jti: string;
    iss: string;
    aud: string[];
    sub: string;
    typ: string;
    azp: string;
    nonce: string;
    session_state: string;
    acr: string;
    resource_access: Record<string, { roles: string[] }>;
    scope: string;
    sid: string;
    email_verified: boolean;
    name: string;
    preferred_username: string;
    given_name: string;
    family_name: string;
    email: string;
}

const authContextObject = React.createContext<any>({
    init: () => {},
    login: () => {},
    logout: () => {},
    getToken: () => {},
    getRoles: {},
    roles: () => {},
    isInitialized: false,
    isAuthenticated: () => false,
    hasRequiredRoles: (): boolean => {
        return false;
    },
});
interface AuthContext {
    init: (x: { clientId: string; realm: string; url: string }) => void;
    login: () => void;
    logout: () => void;
    getToken: () => any; // You can replace 'any' with the actual token type
    getRoles: () => string[];
    roles: () => void;
    isInitialized: boolean;
    isAuthenticated: () => boolean;
    hasRequiredRoles: (requiredRoles: string[]) => boolean;
    getUserName: () => string;
    getUserEmail: () => string;
    keycloakLoading: boolean;
    keycloakError: string;
}
const useAuth = (): AuthContext => useContext(authContextObject);

const AuthContextProvider = ({ children }: any): React.JSX.Element => {
    const [currentClientId, setCurrentClientId] = useState('');
    const isKeycloakInitialized = useRef(false);
    const [keycloak, setKeycloak] = useState<any>();
    const [roles, setRoles] = useState<string[]>([]);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [keycloakLoading, setKeycloakLoading] = useState<boolean>(false);
    const [keycloakError, setKeycloakError] = useState<string>('');
    const isAuthenticated = (): boolean => keycloak?.authenticated ?? false;
    const dedicatedEnv =
        (process.env.REACT_APP_DEDICATED_ENV_TYPE ?? 'false') === 'true';

    const logout = (): void => {
        const realm = localStorage.getItem('realm');
        const redirectUri = realm
            ? `${window.location.origin}/?realm=${realm}`
            : window.location.origin;

        if (!dedicatedEnv) localStorage.clear();

        keycloak.logout({
            clientId: currentClientId,
            postLogoutRedirectUri: redirectUri,
        });
    };
    const getUpdateToken = (minVal: number): string => {
        keycloak.updateToken(minVal);
        return keycloak.token;
    };
    const getToken = (): string => {
        if (keycloak.isTokenExpired(TOKEN_MIN_VALIDITY_BEFORE_EXPIRATION)) {
            return getUpdateToken(TOKEN_MIN_VALIDITY_TO_FORCE_UPDATE);
        } else {
            // DO NOT REMOVE - leaving this for future testing purposes to check the expiration time of the current token.
            // const expiresIn = keycloak.tokenParsed['exp'] - Math.ceil(new Date().getTime() / 1000) + keycloak.timeSkew;
            return keycloak.token;
        }
    };

    const getRoles = (): string[] => {
        return roles;
    };

    const getUserName = (): string => {
        return keycloak.idTokenParsed.name;
    };

    const getUserEmail = (): string => {
        return keycloak?.idTokenParsed?.email ?? '';
    };

    // Assigns user roles and puts them in localStorage so even pure functions can consumem them.
    const parseRoles = (token: string): void => {
        if (!token) {
            setRoles([]);
        } else {
            let parsedRoles = [];
            const decodedToken: TokenData = jwt_decode(token);
            const currentEnvString: string = localStorage.getItem('env') || '';

            if (currentEnvString) {
                const currentEnv = JSON.parse(currentEnvString)?.sdz || '';

                parsedRoles =
                    decodedToken?.resource_access[currentEnv]?.roles || [];
                setRoles(parsedRoles);
                localStorage.setItem('roles', JSON.stringify(parsedRoles));
            }
        }
    };

    const initKeycloak = (config: {
        clientId: string;
        realm: string;
        url: string;
    }): void => {
        // Don't try to initialize keycloak again if it has been already initialized.
        if (isKeycloakInitialized.current) {
            return;
        }

        setKeycloakLoading(true);
        const { clientId } = config;
        const keycloakLocal = new Keycloak(config);
        keycloakLocal
            .init({ onLoad: 'login-required' })
            .then(() => {
                setKeycloak(keycloakLocal);
                parseRoles(keycloakLocal?.token ?? '');
                setIsInitialized(true);
                setKeycloakLoading(false);
                setCurrentClientId(clientId);
            })
            .catch((error) => {
                setKeycloakLoading(false);
                setKeycloakError(error.error);
            });
        isKeycloakInitialized.current = true;
    };

    const lcaseRoles = (roleList: string[]): string[] => {
        return roleList.map((role: string) => {
            return role.toLowerCase();
        });
    };

    const hasRequiredRoles = (requiredRoles: string[]): boolean => {
        // looks like there are no required roles so return true;
        if (!requiredRoles.length) {
            return true;
        }

        const userRoles = lcaseRoles(getRoles());
        const hasNeededRoles = lcaseRoles(requiredRoles).filter(
            (element: string) => userRoles.includes(element)
        )?.length;

        return !!hasNeededRoles;
    };

    return (
        <authContextObject.Provider
            value={{
                logout: logout,
                getToken: getToken,
                init: (x: { clientId: string; realm: string; url: string }) =>
                    initKeycloak(x),
                getRoles: getRoles,
                roles: (): void => {},
                hasRequiredRoles: hasRequiredRoles,
                isInitialized: isInitialized,
                isAuthenticated: isAuthenticated,
                getUserName: getUserName,
                getUserEmail: getUserEmail,
                keycloakLoading: keycloakLoading,
                keycloakError: keycloakError,
            }}
        >
            {children}
        </authContextObject.Provider>
    );
};

export { useAuth, AuthContextProvider };
